import { memo } from "react";
import { Select } from "antd";
import { useTranslation } from "react-i18next";
import type { Station } from "../../../../../api/stations";
import type { Device } from "../../../../../api/devices";
import { getTranslation } from "../../../../../utils/translations";
import { languageLabel, languageName } from "../../../../../utils/languageLabel";
import { PosKitchenTicketExample } from "./PosPrintExamples";
import { ChefHatIcon, CheckIcon } from "../modal/deviceModalIcons";
import {
  EYEBROW_CLASS,
  DeviceExampleBlock,
  DeviceSectionCard,
  DeviceSectionHeader,
} from "../modal/deviceModalParts";

// Езикът по подразбиране на печата, когато не е избран друг.
const DEFAULT_PRINT_LANGUAGE = "bg";

interface Props {
  stations: Station[];
  selectedStationIds: number[];
  onSelectedStationsChange: (ids: number[]) => void;
  languageCodes: string[];
  printLanguage: string | null;
  onPrintLanguageChange: (code: string | null) => void;
  // За обяснението до примера: името на този принтер и друг POS принтер със станции.
  printerName: string;
  otherPrinter: Device | null;
  isNarrow: boolean;
}

/**
 * Секция „Кухненски билети" — кои станции печатат тук и на какъв език.
 * Не зависи от акаунтите и от статуса им.
 */
const PosKitchenSection = ({
  stations,
  selectedStationIds,
  onSelectedStationsChange,
  languageCodes,
  printLanguage,
  onPrintLanguageChange,
  printerName,
  otherPrinter,
  isNarrow,
}: Props) => {
  const { t, i18n } = useTranslation();
  const ticketLanguage = printLanguage ?? DEFAULT_PRINT_LANGUAGE;
  const selectedStations = stations.filter((station) =>
    selectedStationIds.includes(station.id),
  );
  const allSelected = stations.length > 0 && selectedStations.length === stations.length;
  const sectionPadding = isNarrow ? "p-4" : "px-4 py-3";

  const toggleStation = (stationId: number) =>
    onSelectedStationsChange(
      selectedStationIds.includes(stationId)
        ? selectedStationIds.filter((id) => id !== stationId)
        : [...selectedStationIds, stationId],
    );

  return (
    <DeviceSectionCard>
      <DeviceSectionHeader
        icon={<ChefHatIcon size={18} />}
        iconClassName="bg-ok-tint text-ok-deep"
        title={t("devices.posModal.kitchenTitle")}
        description={t("devices.posModal.kitchenDesc")}
        shortDescription={t("devices.posModal.kitchenDescShort")}
        isNarrow={isNarrow}
      />

      {!isNarrow && (
        <DeviceExampleBlock>
          <PosKitchenTicketExample
            languageCode={ticketLanguage}
            languageName={languageName(ticketLanguage, i18n.language)}
            stationNames={selectedStations.map((station) =>
              getTranslation(station.translations, ticketLanguage, station.slug),
            )}
            printerName={printerName}
            printerStationNames={selectedStations.map((station) =>
              getTranslation(station.translations, i18n.language, station.slug),
            )}
            otherPrinter={
              otherPrinter && {
                name: otherPrinter.name,
                stationNames: stations
                  .filter((station) => otherPrinter.stationIds.includes(station.id))
                  .map((station) => getTranslation(station.translations, i18n.language, station.slug)),
              }
            }
          />
        </DeviceExampleBlock>
      )}

      <div className={`flex flex-col ${isNarrow ? "gap-4" : "gap-3"} ${sectionPadding}`}>
        <div className={`flex flex-col ${isNarrow ? "gap-2.5" : "gap-2"}`}>
          <div className="flex items-center gap-2">
            <span className={EYEBROW_CLASS}>{t("devices.posModal.stationsLabel")}</span>
            <span className="rounded-full bg-ok-tint px-[9px] py-[3px] text-[11px] font-bold text-ok-deep">
              {t("devices.posModal.stationsSelected", { count: selectedStations.length })}
            </span>
            {stations.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  onSelectedStationsChange(allSelected ? [] : stations.map((station) => station.id))
                }
                className="ml-auto cursor-pointer rounded-[6px] px-1.5 py-1 text-[12.5px] font-semibold text-body hover:bg-shade-1"
              >
                {allSelected ? t("devices.posModal.removeAll") : t("devices.posModal.selectAll")}
              </button>
            )}
          </div>
          {stations.length === 0 ? (
            <div className="rounded-[10px] bg-canvas p-6 text-center text-[13px] text-body">
              {t("devices.noStations")}
            </div>
          ) : (
            <div
              className={`grid gap-1.5 ${
                // Десктоп: колона ≥ 130px (4 на ред), за да се събере без скрол.
                isNarrow ? "grid-cols-2" : "grid-cols-[repeat(auto-fill,minmax(130px,1fr))]"
              }`}
            >
              {stations.map((station) => {
                const isSelected = selectedStationIds.includes(station.id);
                const stationName = getTranslation(station.translations, i18n.language, station.slug);
                return (
                  <button
                    key={station.id}
                    type="button"
                    onClick={() => toggleStation(station.id)}
                    // Пълното име при посочване — на десктоп дългото се реже.
                    title={stationName}
                    className={`flex cursor-pointer items-center rounded-[10px] border ${
                      isNarrow ? "h-12 gap-2 px-2.5" : "h-10 gap-1.5 px-2"
                    } ${
                      isSelected
                        ? "border-ok/40 bg-ok-tint"
                        : "border-shade-3 bg-surface hover:border-ok/40"
                    }`}
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      // Цветът на станцията идва от данните.
                      style={{ background: station.color }}
                    />
                    <span
                      className={`min-w-0 flex-1 truncate text-left font-semibold text-ink ${
                        isNarrow ? "text-[13px]" : "text-[12.5px]"
                      }`}
                    >
                      {stationName}
                    </span>
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-[1.5px] text-white ${
                        isSelected ? "border-ok bg-ok" : "border-shade-5 bg-surface"
                      }`}
                    >
                      {isSelected && <CheckIcon size={11} strokeWidth={3.5} />}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="text-[11.5px] leading-[1.4] text-muted">
            {t("devices.posModal.stationsNote")}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className={EYEBROW_CLASS}>{t("devices.posModal.languageLabel")}</span>
          <Select
            allowClear
            showSearch
            placeholder={t("devices.printLanguagePlaceholder")}
            value={printLanguage}
            onChange={(code) => onPrintLanguageChange(code ?? null)}
            className="w-full"
            filterOption={(input, option) =>
              typeof option?.label === "string" &&
              option.label.toLowerCase().includes(input.toLowerCase())
            }
            notFoundContent={t("devices.noLanguages")}
            options={languageCodes.map((code) => ({
              value: code,
              label: languageLabel(code, i18n.language),
            }))}
          />
          <div className="text-[11.5px] leading-[1.4] text-muted">
            {t("devices.posModal.languageNote")}
          </div>
        </div>
      </div>
    </DeviceSectionCard>
  );
};

// memo: при писане на името в хедъра секцията не се прерисува.
export default memo(PosKitchenSection);
